import { useStoreAnalytics } from '@/hooks/features/analytics'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'

import { ErrorsCard, SectionsCard, SpeedCard } from '../AnalyticsCards'
import { titleClass } from '../constants'
import { LowSample, type AnalyticsFilters, type QueryState } from '../parts'

import type { AnalyticsErrors, AnalyticsPages, AnalyticsSectionRow, AnalyticsThemeChange } from '@/types/analytics'

/** Is the store fast and working: page speed, the sections shoppers see, the funnel per design version, and errors. */
export const HealthTab = ({ range, filters, errors }: { range: AnalyticsFilters; filters: AnalyticsFilters; errors: QueryState<AnalyticsErrors> }) => {
  const pages = useStoreAnalytics<AnalyticsPages>('pages', filters)
  const sections = useStoreAnalytics<AnalyticsSectionRow[]>('sections', { ...range })
  const themes = useStoreAnalytics<AnalyticsThemeChange[]>('theme-changes', { ...range })

  return (
    <div className="space-y-4">
      <SpeedCard q={pages} />

      <ErrorsCard q={errors} />

      <SectionsCard q={sections} />

      <Card>
        <CardHeader>
          <CardTitle className={titleClass}>Store design changes</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Each time you save a new theme, section layout or style, the funnel is tracked separately. A change in conversion after a design change can also come from
            the season, prices or ads, so treat it as a lead to look into, not proof.
          </p>
          {themes.isLoading ? (
            <Skeleton className="h-32" />
          ) : themes.isError ? (
            <p className="text-sm text-error">{themes.error.message}</p>
          ) : !themes.data?.length ? (
            <p className="py-6 text-center text-sm text-muted-foreground">No design changes with visits in these dates.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Design saved</TableHead>
                  <TableHead>Theme</TableHead>
                  <TableHead className="text-right">Sessions</TableHead>
                  <TableHead className="text-right">Added to cart</TableHead>
                  <TableHead className="text-right">Reached checkout</TableHead>
                  <TableHead className="text-right">Loading (mobile)</TableHead>
                  <TableHead className="text-right">Conversion</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {themes.data.map((t) => (
                  <TableRow key={t.config_version}>
                    <TableCell className="tabular-nums">{new Date(t.published_at).toLocaleString()}</TableCell>
                    <TableCell>{t.theme_key ?? '—'}</TableCell>
                    <TableCell className="text-right tabular-nums">{t.sessions.toLocaleString()}</TableCell>
                    <TableCell className="text-right tabular-nums">{t.add_to_cart_rate}%</TableCell>
                    <TableCell className="text-right tabular-nums">{t.checkout_rate}%</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {(t.mobile_lcp_ms ?? t.lcp_ms) === null ? '—' : `${(((t.mobile_lcp_ms ?? t.lcp_ms) as number) / 1000).toFixed(1)} s`}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {t.conversion_rate}%
                      <LowSample show={t.low_sample} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
